/** Small shared pieces. Nothing here knows about laws specifically. */

import { Link } from "react-router-dom";
import type { ReactNode } from "react";

export function Spinner({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 py-16 text-muted" role="status">
      <span className="size-4 animate-spin rounded-full border-2 border-line border-t-brand" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

/**
 * Failure states say what to do next.
 *
 * A refresh in progress answers 503 with its own wording, which is passed
 * through: "come back in a few minutes" is a different instruction from
 * "something is broken", and the reader can act on the first one.
 */
export function Problem({ error, retry }: { error: Error; retry?: () => void }) {
  return (
    <div className="rounded-xl border border-line bg-card p-6">
      <p className="font-medium">That did not load.</p>
      <p className="mt-1 text-sm text-muted">{error.message}</p>
      {retry && (
        <button
          onClick={retry}
          className="mt-4 rounded-lg bg-ink px-4 py-2 text-sm font-medium text-white hover:bg-ink/85"
        >
          Try again
        </button>
      )}
    </div>
  );
}

/** A law's number, formatted the way it is cited. */
export function LawNumber({ value, className = "" }: { value: string | null; className?: string }) {
  if (!value) return null;
  return (
    <span className={`tnum text-xs font-semibold tracking-wide text-brand uppercase ${className}`}>
      Public Law {value}
    </span>
  );
}

export function Pill({ children, tone = "quiet" }: { children: ReactNode; tone?: "quiet" | "brand" }) {
  const tones = {
    quiet: "bg-sunk text-muted",
    brand: "bg-brand-soft text-brand",
  };
  return (
    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${tones[tone]}`}>{children}</span>
  );
}

export function Card({
  to,
  children,
  className = "",
}: {
  to?: string;
  children: ReactNode;
  className?: string;
}) {
  const base =
    "block rounded-2xl border border-line bg-card transition " +
    "hover:border-ink/25 hover:shadow-[0_2px_20px_-8px_rgba(0,0,0,0.18)]";
  return to ? (
    <Link to={to} className={`${base} ${className}`}>
      {children}
    </Link>
  ) : (
    <div className={`rounded-2xl border border-line bg-card ${className}`}>{children}</div>
  );
}
